/**
 * 74.4 (DECISIONS 351): the dressing pieces as the sheet drew them (design_sheets/dressing.ts `buildPiece`), in a tree's
 * model units (1 is NORMAL_TREE_PERSONS people), base at y = 0, in the biome's colours. The log and the shrub are flora in
 * the sim (core/biomeDressing.ts: cover and concealment), drawn by the stands as their trees are; the fern bed, the
 * glowcap ring and the bones are drawn only, at a stand tree's foot as its ground cover is (treeStands.ts `treePieces`).
 * The mound is a rock (biomeKit.ts `buildRock`, a rock's units). The log lies centred on its foot, along +x: the sim's
 * obstacle stands at its middle (core/surfaceWalk.ts).
 */
import * as THREE from 'three';
import { BIOME_COLORS } from './biomeKit';
import { blob, leafBlade, lumpy, mat, rng, seg, shade } from './treeFamilies';
import { NORMAL_TREE_PERSONS } from '../constants';
import type { BiomeId, FloraState } from '../types';
import { biomeDressing, isSimPiece, type DrawnPiece, type RockWeather } from '../core/biomeDressing';
import { SeededRandom } from '../core/random';

export type PieceKind = 'log' | 'shrub' | DrawnPiece;
export const isPieceFlora = (t: string): t is 'log' | 'shrub' => t === 'log' || t === 'shrub';

/** A person in a tree's model units. */
const PER = 1 / NORMAL_TREE_PERSONS;

/** A dressing piece of `kind` in `biome`'s colours, seeded (the stands' prototype seed). */
export function buildPiece(kind: PieceKind, biome: BiomeId, seed: number): THREE.Group {
  const g = new THREE.Group(), r = rng(seed), c = BIOME_COLORS[biome];
  if (kind === 'log') {
    // a trunk lying with its root plate, two snapped limbs up and its cut end pale; centred on its middle
    const len = (5.5 + r() * 1.2) * PER, rad = 0.36 * PER, bark = shade(c.bark, 0.95);
    const body = new THREE.Group(); body.position.x = -len / 2; g.add(body);
    seg(body, rad, rad * 0.75, [0, rad * 0.9, 0], [len, rad * 0.8, (r() - 0.5) * 0.4 * PER], bark, 7);
    const plate = new THREE.Mesh(lumpy(new THREE.CylinderGeometry(0.8 * PER, 0.8 * PER, 0.22 * PER, 8), 0.12, r() * 9), mat(shade(c.bark, 0.7)));
    plate.rotation.z = Math.PI / 2; plate.position.set(-0.05 * PER, 0.72 * PER, 0); body.add(plate);
    blob(body, 0.3 * PER, '#5a4a38', -0.15 * PER, 0.2 * PER, 0, 1, 0, r() * 9);
    for (let k = 0; k < 2; k++) { const x = len * (0.35 + k * 0.3), up = 0.8 + r() * 0.6; seg(body, 0.1 * PER, 0.06 * PER, [x, rad * 1.5, 0], [x + 0.3 * PER, rad + up * PER, (k ? 1 : -1) * 0.4 * PER], bark, 5); }
    seg(body, rad * 0.8, rad * 0.8, [len, rad * 0.8, 0], [len + 0.02 * PER, rad * 0.8, 0], '#cdb68a', 7);
  } else if (kind === 'shrub') {
    // waist-high: three stems under five lumps
    for (let k = 0; k < 3; k++) { const a = k * 2.1 + r(); seg(g, 0.05 * PER, 0.03 * PER, [0, 0, 0], [Math.cos(a) * 0.3 * PER, 0.45 * PER, Math.sin(a) * 0.3 * PER], c.bark, 4); }
    for (let k = 0; k < 5; k++) {
      const a = k * 1.3 + r() * 0.6, d = k === 0 ? 0 : (0.35 + r() * 0.15) * PER, rr = (k === 0 ? 0.48 : 0.36 + r() * 0.08) * PER;
      blob(g, rr, k % 2 ? c.leafDark : c.leaf, Math.cos(a) * d, (k === 0 ? 0.62 : 0.42) * PER, Math.sin(a) * d, 0.8, 0, r() * 9);
    }
  } else if (kind === 'fern') {
    // shin-high: three crowns of four arching fronds
    for (let crown = 0; crown < 3; crown++) {
      const cx = (r() - 0.5) * 0.9 * PER, cz = (r() - 0.5) * 0.9 * PER;
      for (let k = 0; k < 4; k++) {
        const a = (k / 4) * Math.PI * 2 + r(), f = new THREE.Group();
        f.position.set(cx, 0, cz); f.rotation.order = 'YXZ'; f.rotation.y = a; f.rotation.x = 1.0 + r() * 0.3; g.add(f);
        leafBlade(f, 0.16 * PER, 0.75 * PER, k % 2 ? c.leafDark : shade(c.leaf, 1.05), 0, 0.36 * PER, 0, 0.2, 0);
      }
    }
  } else if (kind === 'glowcap') {
    // a ring of six pale-stemmed mushrooms, their caps faintly lit
    const n = 6;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + r() * 0.4, d = (0.5 + r() * 0.15) * PER, h = (0.18 + r() * 0.2) * PER;
      seg(g, 0.035 * PER, 0.03 * PER, [Math.cos(a) * d, 0, Math.sin(a) * d], [Math.cos(a) * d, h, Math.sin(a) * d], '#d8d0c0', 4);
      const m = new THREE.Mesh(new THREE.ConeGeometry((0.09 + r() * 0.05) * PER, 0.08 * PER, 6), mat(c.accent ?? '#e6d89a', 0.6));
      m.position.set(Math.cos(a) * d, h + 0.03 * PER, Math.sin(a) * d); g.add(m);
    }
  } else {
    // a big animal's ribs, spine and skull, knee-high and open
    const bone = '#d8cfba';
    for (let k = 0; k < 6; k++) {
      const x = (k - 2.5) * 0.28 * PER, h = (0.9 - Math.abs(k - 2.5) * 0.12) * PER;
      for (const side of [-1, 1]) seg(g, 0.05 * PER, 0.03 * PER, [x, 0, 0], [x + 0.05 * PER, h, side * 0.45 * PER], bone, 4);
    }
    seg(g, 0.07 * PER, 0.07 * PER, [-0.9 * PER, 0.06 * PER, 0], [0.9 * PER, 0.08 * PER, 0], shade(bone, 0.9), 5);
    const skull = blob(g, 0.3 * PER, bone, 1.5 * PER, 0.18 * PER, 0.5 * PER, 0.75, 0, r() * 9); skull.scale.set(1.5, 0.75, 0.9);
  }
  return g;
}

/** 74.4: what weathers a biome's rocks — its tint (moss the biome's leaf) and how light it lies on the stone. */
const WEATHER_TINT: Record<RockWeather, (b: BiomeId) => string> = { moss: b => BIOME_COLORS[b].leaf, lichen: () => '#b8b48a', dust: () => '#c8a878', ash: () => '#4a4440' };
const WEATHER_LIGHT: Record<RockWeather, number> = { moss: 0.95, lichen: 1.4, dust: 1.3, ash: 0.55 };

/**
 * 74.4 (DECISIONS 351: "today's rocks weathered by biome"): a merged rock's vertex colours (treeStands.ts `mergeModel`)
 * tinted on its upward faces by its biome's weather, in patches; a glowing part (a crystal, a seam) is left as it is. The
 * geometry is changed in place and returned.
 */
export function weatherRock(geo: THREE.BufferGeometry, biome: BiomeId, amount = 0.8): THREE.BufferGeometry {
  const w = biomeDressing(biome).weather;
  const n = geo.attributes.normal as THREE.BufferAttribute, col = geo.attributes.color as THREE.BufferAttribute, pos = geo.attributes.position as THREE.BufferAttribute;
  if (!n || !col) return geo;
  const glow = geo.attributes.glow as THREE.BufferAttribute | undefined, t = new THREE.Color(WEATHER_TINT[w](biome)), tl = (t.r + t.g + t.b) / 3, light = WEATHER_LIGHT[w];
  for (let i = 0; i < n.count; i++) {
    if (glow && glow.getX(i) + glow.getY(i) + glow.getZ(i) > 0) continue;
    const h = Math.sin(pos.getX(i) * 91.3 + pos.getY(i) * 47.9 + pos.getZ(i) * 63.1) * 43758.5453, patch = 0.45 + 0.55 * (h - Math.floor(h));
    const k = Math.min(1, Math.max(0, (n.getY(i) - 0.35) / 0.5)) * amount * patch, lum = (col.getX(i) + col.getY(i) + col.getZ(i)) / 3, s = light * lum / Math.max(1e-6, tl);
    col.setXYZ(i, col.getX(i) + (t.r * s - col.getX(i)) * k, col.getY(i) + (t.g * s - col.getY(i)) * k, col.getZ(i) + (t.b * s - col.getZ(i)) * k);
  }
  col.needsUpdate = true;
  return geo;
}

/**
 * 74.4: the drawn pieces at a stand tree's foot — a fern bed, a glowcap ring, old bones — each at its biome's rate
 * (core/biomeDressing.ts), from the tree's id on a stream of their own (`flora-piece-<id>`: its ground cover's draws as
 * they were); in a tree's model units round its foot. Never in state.
 */
export function drawnPieces(flora: Pick<FloraState, 'id' | 'biome'>): Array<{ kind: DrawnPiece; seed: number; angle: number; dist: number }> {
  const pieces = biomeDressing(flora.biome ?? 'broadleaf').pieces;
  const out: Array<{ kind: DrawnPiece; seed: number; angle: number; dist: number }> = [];
  let rng: SeededRandom | null = null;
  for (const [kind, chance] of pieces) {
    if (isSimPiece(kind)) continue;
    rng ??= new SeededRandom(`flora-piece-${flora.id}`);
    if (rng.range(0, 1) >= chance) continue;
    out.push({ kind, seed: flora.id * 11 + out.length + 1, angle: rng.range(0, Math.PI * 2), dist: rng.range(1.5, 3) * PER });
  }
  return out;
}
